import { evaluateDateTime, timeSiteNow } from '../time.js';
import { DateTime } from 'luxon';
import { LiveState, toLiveState } from '../live.js';
import { matchSpaceTime, parseSpaceTime } from '../spacetime/spacetime.js';
import { sortAndReindex, toNumber } from '../utils/utils.js';
import { slotToDateTime, toSlotKeyDecoded } from '../slotKey.js';
var SlotDefState;
(function (SlotDefState) {
    SlotDefState["available"] = "available";
    SlotDefState["timeLocked"] = "timeLocked";
    SlotDefState["clubLocked"] = "clubLocked";
    SlotDefState["unavailable"] = "unavailable";
    SlotDefState["booked"] = "booked";
    SlotDefState["unmatched"] = "unmatched";
})(SlotDefState || (SlotDefState = {}));
const slotDefStateToLiveState = (timeSlotState) => {
    switch (timeSlotState) {
        case SlotDefState.available:
            return LiveState.available;
        case SlotDefState.booked:
            return LiveState.booked;
        case SlotDefState.timeLocked:
            return LiveState.timeLocked;
        case SlotDefState.unavailable:
            return LiveState.unavailable;
        case SlotDefState.unmatched:
            return LiveState.unavailable;
        case SlotDefState.clubLocked:
            return LiveState.clubLocked;
    }
};
const days = [
    'sunday',
    'monday',
    'tuesday',
    'wednesday',
    'thursday',
    'friday',
    'saturday',
    'sun',
    'mon',
    'tue',
    'wed',
    'thu',
    'fri',
    'sat',
    'søn',
    'man',
    'tir',
    'ons',
    'tor',
    'fre',
    'lør',
    'søndag',
    'mandag',
    'tirsdag',
    'onsdag',
    'torsdag',
    'fredag',
    'lørdag',
    'søndag'
];
const isValidDateValue = (date) => {
    const parts = date.split('-');
    return parts.reduce((result, currentValue) => result && days.includes(currentValue.trim().toLocaleLowerCase()), true);
};
const isValidTimeValue = (time) => {
    const parts = time.split('-');
    return parts.reduce((result, currentValue) => result && /^[0-2][0-9][0]{0,2}$/.test(currentValue), true);
};
const slotDefStateAtDate = (timeSlot, date, now, site, defaultFrom) => {
    try {
        const spaceTime = parseSpaceTime({ str: timeSlot.spaceTime, site });
        if (!matchSpaceTime('', date, spaceTime))
            return SlotDefState.unmatched;
        const startOfValid = evaluateDateTime(timeSlot.recordValidFrom, date, site.spaceTimes);
        const endOfValid = evaluateDateTime(timeSlot.recordValidTo, date, site.spaceTimes, false);
        if (date < startOfValid || date > endOfValid) {
            return SlotDefState.unmatched;
        }
        // Return false if this record is not yet for sale (its valid but just not available)
        if (timeSlot.liveState === LiveState.available ||
            timeSlot.liveState === LiveState.clubLocked) {
            const startOfAvailable = evaluateDateTime(timeSlot.availableFrom || `${defaultFrom}d`, date, site.spaceTimes);
            const endOfAvailable = evaluateDateTime(timeSlot.availableTo, date, site.spaceTimes, false);
            if (now < startOfAvailable || now > endOfAvailable) {
                return SlotDefState.timeLocked;
            }
        }
        switch (timeSlot.liveState) {
            case LiveState.booked:
                return SlotDefState.booked;
            case LiveState.unavailable:
                return SlotDefState.unavailable;
            case LiveState.clubLocked:
                return SlotDefState.clubLocked;
            default:
                return SlotDefState.available;
        }
    }
    catch {
        return SlotDefState.unmatched;
    }
};
const slotIsTimeLocked = (timeSlot, date, now, sst) => {
    if (!timeSlot)
        return false;
    return now < evaluateDateTime(timeSlot.availableFrom, date, sst);
};
const slotDefStateAt = (slot, date, site, defaultFrom) => {
    return slotDefStateAtDate(slot, date, timeSiteNow(), site, defaultFrom);
};
const matchSlotDef = (sieve, slotKey, site, defaultFrom) => {
    const date = slotToDateTime(slotKey);
    // TODO redo this so that we only applies stuf that matchs space times first
    for (const record of sieve.slice().sort(sortFunction)) {
        const recordState = slotDefStateAt(record, date, site, defaultFrom);
        if (recordState !== SlotDefState.unmatched)
            return [recordState, record];
    }
    return [SlotDefState.unmatched, undefined];
};
const matchSlotDefFixed = (sieve, slotKey, site, defaultFrom) => {
    const slot = toSlotKeyDecoded(slotKey);
    const date = slotToDateTime(slot);
    // TODO redo this so that we only applies stuf that matchs space times first
    for (const slot of sieve.slice().sort(sortFunction)) {
        // Convert from fixed to normal just to get this covered by one function
        const st = {
            ...slot,
            price: 0,
            nofTickets: 1,
            groupPrices: '',
            activitySetByFirstTicket: false,
            availableTo: '',
            availableFrom: ''
        };
        const recordState = slotDefStateAt(st, date, site, defaultFrom);
        if (recordState !== SlotDefState.unmatched)
            return [recordState, slot];
    }
    return [SlotDefState.unmatched, undefined];
};
const sortFunction = (a, b) => {
    return (a.id || 0) - (b.id || 0);
};
const makeSpaceTime = (items, dateFilter, hourFilter) => {
    if (!items || !dateFilter || !hourFilter)
        return '';
    return items + '@' + dateFilter + '@' + hourFilter;
};
const newSlotDef = (hourFilter, dateFilter, items, price, id) => {
    return {
        id,
        price,
        spaceTime: makeSpaceTime(items, dateFilter, hourFilter),
        nofTickets: 1,
        slotSpan: 2,
        startOffset: 0,
        endOffset: 0,
        groupPrices: '',
        priceOptions: '',
        activitySetByFirstTicket: false,
        activityId: '',
        recordValidTo: '',
        recordValidFrom: '',
        availableTo: '',
        availableFrom: '',
        reference: '',
        note: '',
        activityFreeText: '',
        liveState: LiveState.available,
        notifications: ''
    };
};
const emptySlotDef = () => {
    const slot = newSlotDef('', '', '', 0, 0);
    slot.spaceTime = '';
    return slot;
};
const toSlotDefs = (records) => {
    if (!Array.isArray(records))
        return [];
    const newRecords = records.map((record) => toTimeSlot(record));
    sortAndReindex(newRecords, 'id');
    // Remove records older than one year (if they have a expiry date)
    const oneYearAgo = DateTime.now().minus({ year: 1 });
    return newRecords.filter((r) => {
        const validTo = DateTime.fromISO(r.recordValidTo);
        return !validTo.isValid || validTo > oneYearAgo;
    });
};
const toTimeSlot = (record) => {
    const slot = emptySlotDef();
    slot.id = record.id;
    slot.price = toNumber(record.price);
    slot.spaceTime = record.spaceTime || '';
    slot.slotSpan = toNumber(record.slotSpan, 2);
    slot.startOffset = toNumber(record.startOffset, 0);
    slot.endOffset = toNumber(record.endOffset, 0);
    slot.nofTickets = toNumber(record.nofTickets, 1);
    slot.activityId = record.activityId || '';
    slot.recordValidFrom = record.recordValidFrom || '';
    slot.recordValidTo = record.recordValidTo || '';
    slot.availableFrom = record.availableFrom || '';
    slot.availableTo = record.availableTo || '';
    slot.reference = record.reference || '';
    slot.note = record.note || '';
    slot.liveState = toLiveState(record.liveState);
    slot.notifications = record.notifications || '';
    // Convert from old format
    if (record.groupPrice && record.groupThreshold && !record.groupPrices) {
        record.groupPrices = `${record.groupThreshold},${record.groupPrice}`;
    }
    // Fix temp format (this did never reach production)
    if (Array.isArray(record.groupPrices)) {
        record.groupPrices = record.groupPrices.join(', ');
    }
    slot.groupPrices = record.groupPrices || '';
    slot.activitySetByFirstTicket = record.activitySetByFirstTicket ?? false;
    slot.activityFreeText = record.activityFreeText || '';
    // Parse new discount format
    if (record.discount && typeof record.discount === 'object') {
        slot.discount = record.discount;
    }
    // Only carried when the slot actually opts in, so round-tripping site config
    // does not add an empty field to every existing slot definition
    if (typeof record.priceOptions === 'string' && record.priceOptions !== '') {
        slot.priceOptions = record.priceOptions;
    }
    // Same treatment for the group restriction, and for the same reason — a site
    // document carries every availability record and RentalSetup rewrites the
    // whole thing on save. See docs/plans/groups.md.
    const restrictedTo = Array.isArray(record.restrictedTo)
        ? record.restrictedTo.filter((id) => typeof id === 'string' && id !== '')
        : [];
    if (restrictedTo.length > 0) {
        slot.restrictedTo = restrictedTo;
    }
    if (typeof record.restrictedFreeText === 'string' &&
        record.restrictedFreeText !== '') {
        slot.restrictedFreeText = record.restrictedFreeText;
    }
    return slot;
};
/**
 * Lightweight version of slotDefStateAtDate for slots that have already been
 * verified to match (spaceTime match + record validity already checked in
 * generateCandidateSlots). Only evaluates the sale window (availableFrom/To)
 * and returns the appropriate SlotDefState.
 */
const slotDefStateForVerifiedSlot = (timeSlot, date, now, site, defaultFrom, hourCache) => {
    try {
        // Skip spaceTime matching and record validity - already verified
        // Only check if the sale window is open
        if (timeSlot.liveState === LiveState.available ||
            timeSlot.liveState === LiveState.clubLocked) {
            const startOfAvailable = evaluateDateTime(timeSlot.availableFrom || `${defaultFrom}d`, date, site.spaceTimes, true, hourCache);
            const endOfAvailable = evaluateDateTime(timeSlot.availableTo, date, site.spaceTimes, false, hourCache);
            if (now < startOfAvailable || now > endOfAvailable) {
                return SlotDefState.timeLocked;
            }
        }
        switch (timeSlot.liveState) {
            case LiveState.booked:
                return SlotDefState.booked;
            case LiveState.unavailable:
                return SlotDefState.unavailable;
            case LiveState.clubLocked:
                return SlotDefState.clubLocked;
            default:
                return SlotDefState.available;
        }
    }
    catch {
        return SlotDefState.unmatched;
    }
};
export { matchSlotDef, matchSlotDefFixed, slotDefStateAt, slotDefStateAtDate, isValidDateValue, isValidTimeValue, newSlotDef, slotIsTimeLocked, emptySlotDef, SlotDefState, slotDefStateToLiveState, slotDefStateForVerifiedSlot, toSlotDefs, toTimeSlot };
//# sourceMappingURL=slotDef.js.map